import { asset, onnx } from './runtime';

// What a label on a challan means, rather than what it says.
//
// Every plant prints its own form. One says "Transporter", the next "Carrier", a third "Bilty
// issued by"; the consignor is variously supplier, sender, shipper, loading point or the mine
// itself. A list of words can only ever cover the forms somebody has already seen, so the labels
// are read by a sentence model instead: each one is turned into a vector, and goes to whichever
// field it lies closest to.
//
// The model runs here, in the browser, on the same runtime as the reader that found the text —
// nothing about anybody's paperwork leaves the machine it was scanned on.

// Each field is described by several ways a form might name it. One phrase is not enough: asked to
// place "Supplier" against a single description of the consignor, the model preferred the
// transporter. Against a handful of real phrasings it is unambiguous, and unseen wordings still
// land on whichever exemplar they are nearest.
export const EXEMPLARS = {
  consignmentId: ['consignment number', 'order number', 'purchase order', 'reference number', 'e-way bill number', 'docket number'],
  transporter: ['transporter', 'carrier', 'haulier', 'fleet owner', 'transport agency', 'logistics provider', 'bilty issued by', 'movement partner'],
  consignor: ['consignor', 'supplier', 'sender', 'shipper', 'despatched from', 'loading point', 'source mine', 'from party'],
  material: ['material', 'description of goods', 'commodity', 'product', 'particulars of goods', 'item description', 'grade'],
  vehicle: ['vehicle number', 'vehicle no', 'truck number', 'lorry number', 'registration number', 'tanker number'],
  quantity: ['quantity', 'qty', 'net weight', 'gross weight', 'quantity despatched'],
  quantityUnit: ['unit', 'unit of measure', 'uom', 'units'],
  driver: ['driver', 'driver name', 'driven by', 'operator name'],
  direction: ['movement', 'movement type', 'inbound or outbound', 'direction of movement', 'nature of movement'],
  challanDate: ['challan date', 'date of challan', 'document date', 'date of issue', 'invoice date'],
};
// Everything else a challan says. Without these the model has to give every heading to its least
// bad field, and "Place of Supply" becomes a consignor. With them, the nearest thing to "Place of
// Supply" is "place of supply", which belongs to nobody and is dropped.
const NOT_A_FIELD = '_none';
export const DECOYS = [
  'place of supply', 'state code', 'due date', 'terms and conditions', 'challan number', 'challan no',
  'invoice number', 'document number', 'bill number',
  'driver signature', 'authorised signatory', 'hsn code', 'sac code', 'serial number', 'rate',
  'amount', 'tax', 'gstin', 'pan', 'address', 'bank details', 'total', 'driver mobile', 'driver licence',
  'consignee', 'billed to', 'ship to', 'buyer', 'reverse charge',
  'billed distance', 'irn', 'acknowledgement number', 'place of delivery',
];
// Below this a label is not close enough to anything to act on. Measured against real challan
// headings: the genuine ones sit above 0.6, the decoys and the noise below 0.45.
const ACCEPT = 0.55;
// Words only, lowercased, the way the model's own vocabulary is built.
const TOKENS = /[a-z0-9]+|[^\sa-z0-9]/g;

const state = {};

const initialize = async () => {
  if (state.ready) return state;
  if (state.failed) throw state.failed;
  try {
    const ort = await onnx();
    const [session, text] = await Promise.all([
      ort.InferenceSession.create(asset('embed.onnx')),
      fetch(asset('embed-vocab.txt')).then((r) => r.text()),
    ]);
    const vocabulary = new Map();
    text.split('\n').forEach((piece, index) => vocabulary.set(piece, index));
    Object.assign(state, {
      ort, session, vocabulary, ready: true, needsTypes: session.inputNames.includes('token_type_ids'),
    });
    return state;
  } catch (failure) {
    // A model that will not download is not a reason to read nothing: the caller falls back to the
    // vocabulary it has always had.
    state.failed = failure;
    throw failure;
  }
};

let loading;
const load = () => {
  if (!loading) loading = initialize().catch((failure) => { loading = null; throw failure; });
  return loading;
};

// WordPiece, as BERT does it: the longest piece of the word that the vocabulary knows, then the
// rest of it marked as a continuation, until the word is used up or nothing fits.
const tokenize = (text, vocabulary) => {
  const ids = [vocabulary.get('[CLS]')];
  (text.toLowerCase().match(TOKENS) || []).forEach((word) => {
    let start = 0;
    const pieces = [];
    while (start < word.length) {
      let end = word.length;
      let found = -1;
      while (start < end) {
        const piece = start === 0 ? word.slice(start, end) : `##${word.slice(start, end)}`;
        if (vocabulary.has(piece)) { found = vocabulary.get(piece); break; }
        end -= 1;
      }
      if (found < 0) { pieces.length = 0; pieces.push(vocabulary.get('[UNK]')); break; }
      pieces.push(found); start = end;
    }
    ids.push(...pieces);
  });
  ids.push(vocabulary.get('[SEP]'));
  return ids;
};

// One vector per phrase: the model's own output averaged over the words it was given, then scaled
// to unit length so two vectors can be compared by multiplying them together.
const embed = async (phrases) => {
  const { ort, session, vocabulary, needsTypes } = await load();
  const rows = phrases.map((phrase) => tokenize(phrase, vocabulary));
  const width = Math.max(...rows.map((row) => row.length));
  const pad = vocabulary.get('[PAD]');
  const ids = new BigInt64Array(rows.length * width);
  const mask = new BigInt64Array(rows.length * width);
  rows.forEach((row, index) => {
    for (let i = 0; i < width; i += 1) {
      ids[index * width + i] = BigInt(i < row.length ? row[i] : pad);
      mask[index * width + i] = BigInt(i < row.length ? 1 : 0);
    }
  });
  const dims = [rows.length, width];
  const feed = {
    input_ids: new ort.Tensor('int64', ids, dims),
    attention_mask: new ort.Tensor('int64', mask, dims),
  };
  if (needsTypes) feed.token_type_ids = new ort.Tensor('int64', new BigInt64Array(rows.length * width), dims);
  const output = await session.run(feed);
  const hidden = output[session.outputNames[0]];
  const depth = hidden.dims[2];
  return rows.map((row, index) => {
    const vector = new Float32Array(depth);
    for (let token = 0; token < row.length; token += 1) {
      const start = (index * width + token) * depth;
      for (let i = 0; i < depth; i += 1) vector[i] += hidden.data[start + i];
    }
    let length = 0;
    for (let i = 0; i < depth; i += 1) { vector[i] /= row.length; length += vector[i] * vector[i]; }
    length = Math.sqrt(length) || 1;
    for (let i = 0; i < depth; i += 1) vector[i] /= length;
    return vector;
  });
};

const closeness = (a, b) => {
  let total = 0;
  for (let i = 0; i < a.length; i += 1) total += a[i] * b[i];
  return total;
};

// The exemplars never change, so their vectors are computed when the models are built and shipped
// beside them. Embedding seventy-five phrases at the gate, on every scan, to arrive at the same
// numbers every time is work nobody should wait for: only what is printed on the paper in front of
// somebody has to be read here.
const known = async () => {
  if (state.exemplars) return state.exemplars;
  const shipped = await fetch(asset('embed-exemplars.json')).then((r) => r.json());
  const bytes = Uint8Array.from(window.atob(shipped.vectors), (c) => c.charCodeAt(0));
  const floats = new Float32Array(bytes.buffer);
  const vectors = shipped.owners.map((unused, index) => floats.subarray(index * shipped.depth, (index + 1) * shipped.depth));
  state.exemplars = { vectors, owners: shipped.owners };
  return state.exemplars;
};

/** Fetches the model before anybody asks for it, so the first scan is not the one that waits. */
export const warm = async () => {
  try { await load(); await known(); } catch (unavailable) { /* the caller falls back */ }
};

/**
 * Whether the model is in hand right now.
 *
 * Asked before a scan uses it, because a scan must never wait for it: the reader is already at work
 * on the page in front of somebody, and twenty-three megabytes arriving over a gate hut's
 * connection is not something to hold a lorry for. Until it lands, the vocabulary answers.
 */
export const ready = () => Boolean(state.ready && state.exemplars);

/**
 * Which field each label means, and how sure that is.
 *
 * @param {string[]} labels the text of each line that might be a label
 * @returns {Array<{ key: string, score: number } | null>} one entry per label, null where the label
 *   is closer to something that is nobody's field than to any field, or too far from everything.
 */
const matchMeanings = async (labels) => {
  if (!labels.length) return [];
  const { vectors, owners } = await known();
  const asked = await embed(labels);
  return asked.map((vector) => {
    const best = new Map();
    vectors.forEach((exemplar, index) => {
      const score = closeness(vector, exemplar);
      const owner = owners[index];
      if (score > (best.get(owner) ?? -1)) best.set(owner, score);
    });
    const ranked = [...best.entries()].sort((a, b) => b[1] - a[1]);
    const [key, score] = ranked[0];
    if (key === NOT_A_FIELD || score < ACCEPT) return null;
    return { key, score };
  });
};

const meaningCache = new Map();
export const meanings = async (labels) => {
  const normalized = labels.map((label) => label.trim().toLowerCase());
  const missing = [...new Set(normalized.filter((label) => !meaningCache.has(label)))];
  if (missing.length) {
    const matches = await matchMeanings(missing);
    missing.forEach((label, index) => meaningCache.set(label, matches[index]));
    while (meaningCache.size > 512) meaningCache.delete(meaningCache.keys().next().value);
    // Keep this call's results even if a very large batch evicted earlier entries.
    const current = new Map(missing.map((label, index) => [label, matches[index]]));
    return normalized.map((label) => (meaningCache.has(label) ? meaningCache.get(label) : current.get(label)));
  }
  return normalized.map((label) => meaningCache.get(label));
};
export default meanings;
